import axios from 'axios';
import { getApiUrl, logApiInfo } from '../config/api';

// API 인스턴스 생성
const createApiInstance = () => {
    const baseURL = getApiUrl();
    
    // API 정보 로깅
    logApiInfo();
    
    return axios.create({
        baseURL,
        timeout: 10000,
        headers: {
            'Content-Type': 'application/json'
        }
    });
};

// API 인스턴스
export const api = createApiInstance();

// Request interceptor
api.interceptors.request.use(
    (config) => {
        console.log('=== API 요청 ===');
        console.log('URL:', config.url);
        console.log('Method:', config.method);
        console.log('Data:', config.data);
        console.log('Headers:', config.headers);
        
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        console.error('요청 인터셉터 에러:', error);
        return Promise.reject(error);
    }
);

// Response interceptor
api.interceptors.response.use(
    (response) => {
        console.log('=== API 응답 성공 ===');
        console.log('Status:', response.status);
        console.log('Data:', response.data);
        return response;
    },
    (error) => {
        console.error('=== API 응답 에러 ===');
        console.error('에러 객체:', error);
        
        if (error.code === 'ERR_NETWORK') {
            console.error('네트워크 에러 - 서버에 연결할 수 없습니다');
        }
        
        if (error.response) {
            console.error('응답 상태:', error.response.status);
            console.error('응답 데이터:', error.response.data);
            
            if (error.response.status === 401) {
                // 로그인 페이지에서는 리다이렉트하지 않음
                if (!window.location.pathname.includes('/login')) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    window.location.href = '/login';
                }
            }
        } else if (error.request) {
            console.error('응답을 받지 못함:', error.request);
        } else {
            console.error('요청 설정 에러:', error.message);
        }
        
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    verify: (token) => api.post('/auth/verify', { token }),
    getMe: () => api.get('/auth/me'),
};

// Transaction API
export const transactionAPI = {
    // 기존 API
    getAll: (params = {}) => api.get('/transactions', { params }),
    getById: (id) => api.get(`/transactions/${id}`),
    create: (data) => api.post('/transactions', data),
    update: (id, data) => api.patch(`/transactions/${id}`, data),
    delete: (id) => api.delete(`/transactions/${id}`),
    getStatistics: (params = {}) => api.get('/transactions/statistics/summary', { params }),
    getUnseenTransactions: (params = {}) => api.get('/transactions/unseen', { params }),
    
    // 안치자 추가 관련 API
    searchForAdditionalBurial: (params = {}) => api.get('/transactions/search-for-additional-burial', { params }),
    createAdditionalBurial: (data) => api.post('/transactions/additional-burial', data),
    
    // 새로운 API (회의 내용 반영)
    getTransactions: (params = {}) => api.get('/transactions', { params }),
    
    // 새로운 통합 비용 항목 API
    getAllCostItems: (id) => api.get(`/transactions/${id}/all-cost-items`),
    
    // 관리비 관리 API
    getManagementFees: (params = {}) => api.get('/management-fees', { params }),
    createManagementFee: (data) => api.post('/management-fees', data),
    updateManagementFee: (id, data) => api.put(`/management-fees/${id}`, data),
    deleteManagementFee: (id) => api.delete(`/management-fees/${id}`),
    
    // 상담 관리 API
    getConsultations: (params = {}) => api.get('/consultations', { params }),
    createConsultation: (transactionId, data) => api.post(`/consultations/${transactionId}`, data),
    updateConsultation: (id, data) => api.put(`/consultations/${id}`, data),
    deleteConsultation: (id) => api.delete(`/consultations/${id}`),
    
    // 민원 관리 API
    getMaintenance: (params = {}) => api.get('/maintenance', { params }),
    createMaintenance: (data) => api.post('/maintenance', data),
    updateMaintenance: (id, data) => api.put(`/maintenance/${id}`, data),
    deleteMaintenance: (id) => api.delete(`/maintenance/${id}`),
    getMaintenanceStatistics: (params = {}) => api.get('/maintenance/statistics/overview', { params }),
    
    // 각자비 관리 API
    getEngraving: (params = {}) => api.get('/engraving', { params }),
    createEngraving: (data) => api.post('/engraving', data),
    updateEngraving: (id, data) => api.put(`/engraving/${id}`, data),
    deleteEngraving: (id) => api.delete(`/engraving/${id}`),
    getEngravingStatistics: (params = {}) => api.get('/engraving/statistics/overview', { params }),
    
    // 비용 통계 API (관리자만)
    getCostStatistics: (params = {}) => api.get('/cost-statistics', { params }),
    getCemeteryCostStatistics: (cemeteryId, params = {}) => api.get(`/cost-statistics/cemetery/${cemeteryId}`, { params }),
    
    // 검색 히스토리 API
    getSearchHistory: (params = {}) => api.get('/search-history', { params }),
    createSearchHistory: (data) => api.post('/search-history', data),
    getPopularSearches: (params = {}) => api.get('/search-history/popular', { params }),
    
    // 계약별 비용 관리 API
    getCosts: (contractId) => api.get(`/contracts/${contractId}/costs`),
    addCost: (contractId, data) => api.post(`/contracts/${contractId}/costs`, data),
    updateCostStatus: (contractId, costId, data) => api.patch(`/contracts/${contractId}/costs/${costId}`, data),
    deleteCost: (contractId, costId) => api.delete(`/contracts/${contractId}/costs/${costId}`),
    updateBasicCostStatus: (contractId, data) => api.patch(`/contracts/${contractId}/basic-cost-status`, data),
    updateStoneCostStatus: (contractId, stoneId, data) => api.patch(`/contracts/${contractId}/costs/${stoneId}`, data),
    updateVaseCostStatus: (contractId, vaseId, data) => api.patch(`/contracts/${contractId}/vases/${vaseId}`, data),
    updateEngravingCostStatus: (contractId, engravingId, data) => api.patch(`/contracts/${contractId}/engravings/${engravingId}`, data),
    updateCostItemStatus: (contractId, costItemId, data) => api.patch(`/contracts/${contractId}/cost-items/${costItemId}`, data),
    // 계약 수정 이력 API
    getHistories: (contractId, params = {}) => api.get(`/transactions/${contractId}/histories`, { params }),
    
    // 계약별 관리비 관리 API
    getManagementFees: (contractId) => api.get(`/contracts/${contractId}/management-fees`),
    addManagementFee: (contractId, data) => api.post(`/contracts/${contractId}/management-fees`, data),
    updateManagementFeeStatus: (contractId, feeId, data) => api.patch(`/contracts/${contractId}/management-fees/${feeId}`, data),
    /** 관리비 수정 (상태·기간·금액·기한·설명) — PATCH 동일 경로 */
    updateManagementFee: (contractId, feeId, data) => api.patch(`/contracts/${contractId}/management-fees/${feeId}`, data),
    deleteManagementFee: (contractId, feeId) => api.delete(`/contracts/${contractId}/management-fees/${feeId}`),
    
    // 계약별 민원 관리 API
    getMaintenance: (contractId) => api.get(`/contracts/${contractId}/maintenance`),
    addMaintenance: (contractId, data) => api.post(`/contracts/${contractId}/maintenance`, data),
    updateMaintenanceStatus: (contractId, maintenanceId, data) => api.patch(`/contracts/${contractId}/maintenance/${maintenanceId}`, data),
    deleteMaintenance: (contractId, maintenanceId) => api.delete(`/contracts/${contractId}/maintenance/${maintenanceId}`),
};

// User API
export const userAPI = {
    getAll: (params = {}) => api.get('/users', { params }),
    getById: (id) => api.get(`/users/${id}`),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.patch(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
};

// 묘지 설정 API
export const cemeteryAPI = {
    getSettings: (cemeteryId) => api.get(`/cemetery-settings/${cemeteryId}`),
    updateSettings: (cemeteryId, data) => api.put(`/cemetery-settings/${cemeteryId}`, data),
};

// 석물 비용 API
export const stoneCostAPI = {
    getStoneCosts: (cemeteryId) => api.get(`/stone-costs/${cemeteryId}`),
    getStoneCost: (cemeteryId, stoneType) => api.get(`/stone-costs/${cemeteryId}/${stoneType}`),
    updateStoneCosts: (cemeteryId, data) => api.put(`/stone-costs/${cemeteryId}`, data),
    createStoneCost: (cemeteryId, data) => api.post(`/stone-costs/${cemeteryId}`, data),
    updateStoneCost: (cemeteryId, costId, data) => api.put(`/stone-costs/${cemeteryId}/${costId}`, data),
    deleteStoneCost: (cemeteryId, costId) => api.delete(`/stone-costs/${cemeteryId}/${costId}`),
};

// 파일 관리 API
export const fileAPI = {
    getUploadUrl: (data) => api.post('/s3/upload-url', data),
    getDownloadUrl: (data) => api.post('/s3/download-url', data),
};

// API 함수들을 transactionAPI에 통합
Object.assign(transactionAPI, {
    // 묘지 설정
    getCemeterySettings: cemeteryAPI.getSettings,
    updateCemeterySettings: cemeteryAPI.updateSettings,
    
    // 석물 비용
    getStoneCosts: stoneCostAPI.getStoneCosts,
    getStoneCost: stoneCostAPI.getStoneCost,
    updateStoneCosts: stoneCostAPI.updateStoneCosts,
    createStoneCost: stoneCostAPI.createStoneCost,
    updateStoneCost: stoneCostAPI.updateStoneCost,
    deleteStoneCost: stoneCostAPI.deleteStoneCost,
    
    // 파일 관리
    getUploadUrl: fileAPI.getUploadUrl,
    getDownloadUrl: fileAPI.getDownloadUrl,
});

// 석물 관리 API (V5)
export const stoneObjectAPI = {
    // 계약별 석물 목록 조회
    getByTransaction: (transactionId) => api.get(`/stone-objects/transaction/${transactionId}`),
    // 석물 생성
    create: (data) => api.post('/stone-objects', data),
    // 석물 수정
    update: (id, data) => api.put(`/stone-objects/${id}`, data),
    // 석물 삭제
    delete: (id) => api.delete(`/stone-objects/${id}`),
    // 석물 순서 변경
    updateOrder: (transactionId, data) => api.patch(`/stone-objects/transaction/${transactionId}/order`, data),
    // 석물 비용 계산
    calculateCost: (data) => api.post('/stone-objects/calculate-cost', data),
};

// 주소 검색 API (V5)
export const addressAPI = {
    // 주소 검색
    search: (params = {}) => api.get('/address/search', { params }),
    // 우편번호 검색
    searchZipcode: (params = {}) => api.get('/address/zipcode', { params }),
    // 좌표로 주소 검색
    searchByCoordinates: (params = {}) => api.get('/address/coordinates', { params }),
    // 주소 정규화
    normalize: (data) => api.post('/address/normalize', data),
};

// 검색 히스토리 API (V5)
export const searchHistoryAPI = {
    // 최근 검색어 조회
    getRecent: (params = {}) => api.get('/search-history/recent', { params }),
    // 인기 검색어 조회
    getPopular: (params = {}) => api.get('/search-history/popular', { params }),
    // 묘원별 인기 검색어
    getCemeteryPopular: (cemeteryId, params = {}) => api.get(`/search-history/cemetery/${cemeteryId}/popular`, { params }),
};

// 전자 계약서 API (V5)
export const contractAPI = {
    // 전자 계약서 발송
    sendEForm: (data) => api.post('/contracts/send-eform', data),
    // eForm 임베딩 설정 조회
    getEFormEmbeddingConfig: () => api.get('/contracts/eform-embedding-config'),
    // eForm Access Token 발급
    getEFormAccessToken: (cemeteryId) => api.post('/contracts/eform-access-token', { cemeteryId }),
};

// 통계 API (V5)
export const statisticsAPI = {
    // 전체 통계 조회
    getAll: (params = {}) => api.get('/statistics', { params }),
    // 매출 통계
    getSales: (params = {}) => api.get('/statistics/sales', { params }),
    // 묘지 형태별 통계
    getTombTypes: (params = {}) => api.get('/statistics/tomb-types', { params }),
    // 지역별 통계
    getRegional: (params = {}) => api.get('/statistics/regional', { params }),
    // 비용 항목별 통계
    getCostBreakdown: (params = {}) => api.get('/statistics/cost-breakdown', { params }),
};

// 필드 설정 API (Field Customization)
export const fieldSettingAPI = {
    // 모든 필드 정의 조회
    getFieldDefinitions: () => api.get('/field-definitions'),
    
    // 카테고리별 필드 정의 조회
    getFieldDefinitionsByCategory: (category) => api.get(`/field-definitions/category/${category}`),
    
    // 묘지별 필드 설정 조회 (기본값 포함)
    getFieldSettingsByCemetery: (cemeteryId) => api.get(`/field-settings/${cemeteryId}`),
    
    // 필드 설정 일괄 업데이트
    updateFieldSettings: (cemeteryId, settings) => api.put(`/field-settings/${cemeteryId}`, { settings }),
    
    // 필드 명칭 업데이트
    updateFieldLabel: (cemeteryId, fieldCode, customLabel) => 
        api.patch(`/field-settings/${cemeteryId}/${fieldCode}/label`, { custom_label: customLabel }),
    
    // 필드 사용 여부 토글
    toggleFieldEnabled: (cemeteryId, fieldCode, isEnabled) => 
        api.patch(`/field-settings/${cemeteryId}/${fieldCode}/enabled`, { is_enabled: isEnabled }),
    
    // 필드 표시 순서 업데이트
    updateDisplayOrder: (cemeteryId, orderUpdates) => 
        api.patch(`/field-settings/${cemeteryId}/display-order`, { order_updates: orderUpdates }),
    
    // 필드 설정 삭제 (기본값으로 되돌리기)
    deleteFieldSetting: (cemeteryId, fieldCode) => 
        api.delete(`/field-settings/${cemeteryId}/${fieldCode}`),
    
    // 묘지 설정 전체 초기화
    resetCemeterySettings: (cemeteryId) => api.delete(`/field-settings/${cemeteryId}`),
};